import { AlertTriangle, Check, Info, ShieldCheck, X } from 'lucide-react';
import type { ValidationIssue, ValidationResult } from '@/hooks/useAi';

interface Props {
  validation: ValidationResult;
  className?: string;
  /** Kompakt: nur 3 Status-Lines, keine Issues-Liste. */
  compact?: boolean;
}

/**
 * ValidationPanel - 3-Check-Anzeige für KI-Playbook-Validation.
 *
 * Zeigt:
 *   1. Schema valid? (JSON-Schema-Validation gegen Playbook-Struktur)
 *   2. Allowlist? (kein shell/raw/command auch nicht in Whitelist)
 *   3. Ansible Syntax-Check? (ansible-playbook --syntax-check)
 *
 * Inline-Issues mit Severity + Hint. Pflichtkomponente für den
 * "Speichern"-Button: nur wenn alle 3 grün, darf gespeichert werden.
 */
export function ValidationPanel({ validation, className, compact = false }: Props): JSX.Element {
  const allValid =
    validation.schema_valid && validation.allowlist_valid && validation.syntax_valid;

  return (
    <section
      className={[
        'rounded-md border p-3',
        allValid
          ? 'border-success/30 bg-success/5'
          : 'border-warning/30 bg-warning/5',
        className ?? '',
      ].join(' ')}
      aria-labelledby="validation-heading"
      data-testid="validation-panel"
      data-valid={allValid ? 'true' : 'false'}
    >
      <header className="mb-2 flex items-center gap-2">
        <ShieldCheck
          className={['size-4', allValid ? 'text-success' : 'text-warning'].join(' ')}
          aria-hidden="true"
        />
        <h3 id="validation-heading" className="text-sm font-semibold">
          Validation
        </h3>
        {allValid ? (
          <span className="ml-auto text-xs text-success">Alle Checks grün</span>
        ) : (
          <span className="ml-auto text-xs text-warning">
            {validation.issues.filter((i) => i.severity === 'error').length} Fehler ·{' '}
            {validation.issues.filter((i) => i.severity === 'warning').length} Warnungen
          </span>
        )}
      </header>

      <ul className="flex flex-col gap-1 text-xs">
        <CheckLine
          ok={validation.schema_valid}
          label="Schema-Validation"
          hint="Playbook erfüllt das JSON-Schema für Ansible-Plays."
        />
        <CheckLine
          ok={validation.allowlist_valid}
          label={
            <span>
              Modul-Allowlist{' '}
              {validation.modules_used.length > 0 && (
                <span className="text-muted">
                  ({validation.modules_used.length} Module:{' '}
                  <span className="font-mono">
                    {validation.modules_used.slice(0, 4).join(', ')}
                    {validation.modules_used.length > 4 ? ', ...' : ''}
                  </span>
                  )
                </span>
              )}
            </span>
          }
          hint={
            validation.modules_blocked.length > 0
              ? `Geblockt: ${validation.modules_blocked.join(', ')}`
              : 'Alle verwendeten Module sind erlaubt.'
          }
        />
        <CheckLine
          ok={validation.syntax_valid}
          label="Ansible Syntax-Check"
          hint="`ansible-playbook --syntax-check` ohne Fehler."
        />
      </ul>

      {!compact && validation.issues.length > 0 && (
        <ul className="mt-3 flex flex-col gap-1.5 border-t border-border pt-2">
          {validation.issues.map((issue, idx) => (
            <IssueRow key={idx} issue={issue} />
          ))}
        </ul>
      )}
    </section>
  );
}

interface CheckLineProps {
  ok: boolean;
  label: React.ReactNode;
  hint?: string;
}

function CheckLine({ ok, label, hint }: CheckLineProps): JSX.Element {
  return (
    <li className="flex items-start gap-2">
      {ok ? (
        <Check className="mt-0.5 size-3.5 shrink-0 text-success" aria-hidden="true" />
      ) : (
        <X className="mt-0.5 size-3.5 shrink-0 text-danger" aria-hidden="true" />
      )}
      <span className="flex flex-col gap-0.5">
        <span className={ok ? 'text-text' : 'font-medium text-danger'}>{label}</span>
        {hint && <span className="text-muted">{hint}</span>}
      </span>
    </li>
  );
}

function IssueRow({ issue }: { issue: ValidationIssue }): JSX.Element {
  const Icon =
    issue.severity === 'error' ? X : issue.severity === 'warning' ? AlertTriangle : Info;
  const tone =
    issue.severity === 'error'
      ? 'text-danger'
      : issue.severity === 'warning'
        ? 'text-warning'
        : 'text-primary';
  return (
    <li
      className={['flex items-start gap-2 text-xs', tone].join(' ')}
      data-testid={`validation-issue-${issue.severity}`}
    >
      <Icon className="mt-0.5 size-3 shrink-0" aria-hidden="true" />
      <span>
        <span className="font-medium uppercase tracking-wide">[{issue.check}]</span>{' '}
        <span>{issue.message}</span>
        {issue.line != null && (
          <span className="text-muted"> (Zeile {issue.line})</span>
        )}
        {issue.hint && (
          <span className="block text-muted">Hinweis: {issue.hint}</span>
        )}
      </span>
    </li>
  );
}
